// front-end/src/context/SupabaseAuthContext.tsx
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../config/supabase';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';

// Definizione locale dell'interfaccia UserProfile
interface UserProfile {
  id: string;
  email: string;
  username: string;
  nome: string;
  cognome: string;
  ruolo: 'admin' | 'editor' | 'viewer';
  attivo: boolean;
  ultimo_accesso?: string;
  created_at: string;
  updated_at: string;
}

export interface User {
  id: string;
  email: string;
  username: string;
  nome: string;
  cognome: string;
  nomeCompleto: string;
  ruolo: 'admin' | 'editor' | 'viewer';
  attivo: boolean;
  ultimoAccesso?: string;
  createdAt: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
  session: Session | null;
}

export interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<boolean>;
  updatePassword: (newPassword: string) => Promise<boolean>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  getAuthToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function SupabaseAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    loading: true,
    error: null,
    session: null
  });

  // Funzione per convertire profilo Supabase in User
  const convertToUser = (supabaseUser: SupabaseUser, profile: UserProfile): User => {
    return {
      id: supabaseUser.id,
      email: profile.email,
      username: profile.username,
      nome: profile.nome,
      cognome: profile.cognome,
      nomeCompleto: `${profile.nome} ${profile.cognome}`,
      ruolo: profile.ruolo,
      attivo: profile.attivo,
      ultimoAccesso: profile.ultimo_accesso,
      createdAt: profile.created_at
    };
  };

  // Carica sessione e profilo utente
  const loadUserSession = async (session: Session | null, isInitialLoad = false) => {
    console.log('loadUserSession chiamata con:', { session: !!session, isInitialLoad });
    
    // Imposta loading solo se è il caricamento iniziale
    if (isInitialLoad) {
      setState(prev => ({ ...prev, loading: true, error: null }));
    }

    if (!session?.user) {
      console.log('Nessuna sessione trovata, imposto stato non autenticato');
      setState({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
        session: null
      });
      return;
    }

    try {
      console.log('Verifica della sessione con Supabase per utente:', session.user.id);
      
      // Usa direttamente l'utente dalla sessione invece di fare una nuova chiamata
      const user = session.user;
      console.log('Utente dalla sessione:', { id: user.id, email: user.email });
      
      if (!user) {
        console.log('Nessun utente nella sessione');
        throw new Error('Nessun utente nella sessione');
      }

      // Crea profilo direttamente dai dati Supabase Auth (no database dipendenza)
      console.log('Creazione profilo dai dati Supabase Auth');
      
      const userProfile: UserProfile = {
        id: user.id,
        email: user.email!,
        username: user.email!.split('@')[0],
        nome: user.user_metadata?.nome || user.user_metadata?.name || '',
        cognome: user.user_metadata?.cognome || user.user_metadata?.surname || '',
        ruolo: 'admin' as const, // Default admin per ora
        attivo: true,
        ultimo_accesso: new Date().toISOString(),
        created_at: user.created_at,
        updated_at: new Date().toISOString()
      };
      
      console.log('Profilo creato:', userProfile);

      const userData = convertToUser(user, userProfile);
      console.log('✅ Autenticazione completata con successo per:', userData.email);

      setState({
        isAuthenticated: true,
        user: userData,
        loading: false,
        error: null,
        session
      });
    } catch (error: any) {
      console.error('Errore durante il caricamento della sessione:', error);
      
      setState({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: error.message || 'Errore di autenticazione',
        session: null
      });
    }
  };

  // Login
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      if (data.session) {
        await loadUserSession(data.session, false);
        return true;
      }

      return false;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Errore durante il login'
      }));
      return false;
    }
  };

  // Logout
  const logout = async (): Promise<void> => {
    try {
      await supabase.auth.signOut();
      setState({
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
        session: null
      });
    } catch (error: any) {
      console.error('Errore durante logout:', error);
      setState(prev => ({
        ...prev,
        error: error.message
      }));
    }
  };

  // Signup rimosso - registrazione non permessa

  // Reset password
  const resetPassword = async (email: string): Promise<boolean> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) throw error;
      return true;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || 'Errore durante il reset password'
      }));
      return false;
    }
  };

  // Update password
  const updatePassword = async (newPassword: string): Promise<boolean> => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      return true;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message || 'Errore durante l\'aggiornamento password'
      }));
      return false;
    }
  };

  // Refresh user
  const refreshUser = async (): Promise<void> => {
    console.log('Refresh utente chiamato');
    const { data: { session } } = await supabase.auth.getSession();
    await loadUserSession(session, false);
  };

  // Clear error
  const clearError = (): void => {
    setState(prev => ({ ...prev, error: null }));
  };

  // Get auth token
  const getAuthToken = (): string | null => {
    return state.session?.access_token || null;
  };

  // Setup auth listener
  useEffect(() => {
    let isMounted = true;
    console.log('Inizializzazione context di autenticazione (isMounted:', isMounted, ')');
    console.log('Supabase client:', supabase);
    console.log('Supabase auth:', supabase.auth);
    
    // Carica sessione iniziale
    console.log('Tentativo di ottenere sessione iniziale...');
    
    let sessionResolved = false;
    
    // Timeout di sicurezza per evitare hang infiniti
    const sessionTimeout = setTimeout(() => {
      if (!sessionResolved && isMounted) {
        console.warn('TIMEOUT: getSession() non ha risposto in 3 secondi, forzo no-session');
        sessionResolved = true;
        loadUserSession(null, true);
      }
    }, 3000);
    
    // Forza chiamata anche se getSession è bloccata
    const forceCheck = setTimeout(() => {
      if (!sessionResolved && isMounted) {
        console.warn('FORCE: getSession() bloccata, provo direttamente localStorage');
        const storedSession = localStorage.getItem('sb-flwwivrgccqacrcjoaot-auth-token');
        console.log('Sessione in localStorage:', !!storedSession);
        if (!storedSession) {
          sessionResolved = true;
          clearTimeout(sessionTimeout);
          loadUserSession(null, true);
        }
      }
    }, 1000);
    
    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        if (sessionResolved) return;
        sessionResolved = true;
        clearTimeout(sessionTimeout);
        clearTimeout(forceCheck);
        console.log('✅ Risposta getSession:', { session: !!session, error });
        if (error) {
          console.error('Errore getSession:', error);
          setState(prev => ({ ...prev, loading: false, error: error.message }));
          return;
        }
        console.log('Sessione iniziale caricata:', !!session);
        loadUserSession(session, true);
      })
      .catch((error) => {
        if (sessionResolved) return;
        sessionResolved = true;
        clearTimeout(sessionTimeout);
        clearTimeout(forceCheck);
        console.error('❌ Errore catch getSession:', error);
        // Anche in caso di errore, continua senza sessione
        loadUserSession(null, true);
      });

    // Ascolta cambiamenti di autenticazione
    console.log('Configurazione listener auth state change...');
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth event ricevuto:', event, 'Sessione presente:', !!session);
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        await loadUserSession(session, false);
      } else if (event === 'SIGNED_OUT') {
        console.log('Utente disconnesso');
        setState({
          isAuthenticated: false,
          user: null,
          loading: false,
          error: null,
          session: null
        });
      }
    });

    console.log('Auth listener configurato:', subscription);

    return () => {
      console.log('Cleanup auth listener');
      isMounted = false;
      clearTimeout(sessionTimeout);
      clearTimeout(forceCheck);
      subscription.unsubscribe();
    };
  }, []);

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    resetPassword,
    updatePassword,
    refreshUser,
    clearError,
    getAuthToken
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook per usare il context
export function useSupabaseAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useSupabaseAuth deve essere usato dentro SupabaseAuthProvider');
  }
  return context;
}

// Hook per permessi
export function useSupabasePermissions() {
  const { user } = useSupabaseAuth();

  return {
    isAdmin: user?.ruolo === 'admin',
    isEditor: user?.ruolo === 'editor' || user?.ruolo === 'admin',
    isViewer: user?.ruolo === 'viewer' || user?.ruolo === 'editor' || user?.ruolo === 'admin',
    canManageUsers: user?.ruolo === 'admin',
    canEditContent: user?.ruolo === 'editor' || user?.ruolo === 'admin',
    canViewStats: user?.ruolo === 'viewer' || user?.ruolo === 'editor' || user?.ruolo === 'admin'
  };
}

export default AuthContext;